import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {useFlagsCookie} from "@khan/data-cookies";

import {useGrowthBookForABTest} from "./use-growthbook-for-ab-test.ts";

/**
 * Determines if the user is in an A/B test or not. This also supports using
 * the query param ?flags_on=<feature name> to force the new experience to
 * display.
 * - If true, you're in the experiment.
 * - If false, you're not in the experiment.
 * - If null, we don't know yet, hold off on rendering so you don't have a FOUC,
 * or show the default experience.
 * @param {string & keyof AppFeatures} featureKey - The name of the feature as defined in
 * GrowthBook.
 * @param {?boolean} skip - The ability to bypass enrolling the user into the experiment,
 * for example, ineligible users.
 */
export function useBooleanABTestOrCookie(
    featureKey: string & keyof AppFeatures,
    skip?: boolean,
): boolean | null {
    // Checking the useFlagsCookie hook allows us to use the query param:
    // ?flags_on=new-registration-flow to force the new registration flow.
    const flagsCookieResult = useFlagsCookie(featureKey);
    const growthBook = useGrowthBookForABTest();

    const flagsCookie =
        flagsCookieResult.status === "loading"
            ? false
            : flagsCookieResult.value;

    if (skip) {
        return flagsCookie || false;
    }

    if (!growthBook) {
        return null;
    }

    // KAID is ready, so we can now get the feature value.
    const featureValue = growthBook.getFeatureValueAndEnrollInABTest(
        featureKey,
        false,
    );

    return flagsCookie || featureValue;
}
